import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDebouncedFunction } from '@client/hooks/use-utils.hooks';
import { sleep } from '@shared/helpers/promise.helpers';
import {
  captureReportPageThumbnail,
  getReportPageCaptureElement,
  getReportPagesLayoutSignature,
  getReportScrollAnchorIndex,
  getReportThumbnailCanvasSize,
  REPORT_SCROLL_ANCHOR_ATTR,
} from '../helpers/capture-report-page-thumbnail.helpers';
import {
  getReportThumbnailCache,
  hasCompleteReportThumbnailCache,
  writeReportThumbnailCacheLayoutSignature,
  writeReportThumbnailCacheUrl,
} from '../helpers/report-page-thumbnail-cache.helpers';
import { buildReportThumbnailFontEmbedCss } from '../helpers/report-thumbnail-font-embed.helpers';

const INPUT_DEBOUNCE_MS = 200;
const RESIZE_DEBOUNCE_MS = 600;
/** Extra wait after size is stable so `cqw` / aspect-ratio finish restyling. */
const LAYOUT_SETTLE_MS = 800;
const STABLE_LAYOUT_FRAMES = 3;
const MAX_LAYOUT_POLL_FRAMES = 45;
/** Cover Mapbox/ThreeJS often paints after the first HTML snapshot. */
const CANVAS_FOLLOW_UP_MS = [800, 2200] as const;

type ThumbnailSlot = string | null | undefined;

type UseReportPageThumbnailsArgs = {
  mainRef: RefObject<HTMLDivElement | null>;
  /** Visible page keys in canvas order. Slots are stored by these, not index. */
  pageKeys: readonly string[];
  thumbWidthPx: number;
  /**
   * Value-stable key for the report's visual content (not React children).
   * When it matches the module cache, remount skips recapture.
   */
  contentKey: string;
};

const getScrollAnchors = (container: HTMLElement): HTMLElement[] => [
  ...container.querySelectorAll<HTMLElement>(
    `:scope > [${REPORT_SCROLL_ANCHOR_ATTR}]`,
  ),
];

const waitNextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });

/**
 * Wait until page boxes keep the same pixel size, then pause so container-query
 * units settle. Pages animate `aspect-ratio` for 300ms on mount.
 * Uses {@link getReportPagesLayoutSignature} to detect size changes.
 */
const waitForSettledPageLayout = async (
  container: HTMLElement,
  isCancelled: () => boolean,
): Promise<void> => {
  let previous = '';
  let stableFrames = 0;
  for (let i = 0; i < MAX_LAYOUT_POLL_FRAMES; i += 1) {
    if (isCancelled()) return;
    await waitNextFrame();
    const anchors = getScrollAnchors(container);
    const signature = getReportPagesLayoutSignature(anchors);
    const hasBox = anchors.some(
      (anchor) => getReportPageCaptureElement(anchor).offsetWidth > 0,
    );
    if (hasBox && signature === previous) {
      stableFrames += 1;
      if (stableFrames >= STABLE_LAYOUT_FRAMES) break;
    } else {
      stableFrames = 0;
      previous = signature;
    }
  }
  if (isCancelled()) return;
  await sleep(LAYOUT_SETTLE_MS);
};

/**
 * PNG data URLs for the sidebar thumbnails, captured from the main-pane pages.
 * `undefined` slots are still capturing; `null` means capture finished empty.
 */
export const useReportPageThumbnails = ({
  mainRef,
  pageKeys,
  thumbWidthPx,
  contentKey,
}: UseReportPageThumbnailsArgs): ThumbnailSlot[] => {
  const pageKeysSignature = pageKeys.join('\0');
  const pageKeysRef = useRef(pageKeys);
  pageKeysRef.current = pageKeys;

  const [urlsByPageKey, setUrlsByPageKey] = useState<
    Record<string, ThumbnailSlot>
  >(
    () =>
      getReportThumbnailCache(contentKey, pageKeys, thumbWidthPx)
        ?.urlsByPageKey ?? {},
  );
  const generationRef = useRef(0);
  const thumbWidthRef = useRef(thumbWidthPx);
  thumbWidthRef.current = thumbWidthPx;
  const fontEmbedCssRef = useRef<string | undefined>(undefined);
  const layoutReadyRef = useRef(false);

  const setSlotAt = useCallback(
    (pageKey: string, url: string | null) => {
      if (url) {
        writeReportThumbnailCacheUrl(
          contentKey,
          pageKeysRef.current,
          thumbWidthRef.current,
          pageKey,
          url,
        );
      }
      setUrlsByPageKey((prev) => {
        if (prev[pageKey] === url) return prev;
        return { ...prev, [pageKey]: url };
      });
    },
    [contentKey],
  );

  const captureIndex = useCallback(
    async (index: number, generation: number) => {
      const container = mainRef.current;
      if (!container || generation !== generationRef.current) return;
      const pageKey = pageKeysRef.current[index];
      if (!pageKey) return;
      const element = getScrollAnchors(container)[index];
      if (!element) return;
      const captureTarget = getReportPageCaptureElement(element);
      const canvasSize = getReportThumbnailCanvasSize({
        pageWidthPx: captureTarget.offsetWidth,
        pageHeightPx: captureTarget.offsetHeight,
        thumbWidthPx: thumbWidthRef.current,
        devicePixelRatio: window.devicePixelRatio || 1,
      });
      // No box yet — keep the slot pending so ResizeObserver can retry.
      if (!canvasSize) return;
      const url = await captureReportPageThumbnail(captureTarget, {
        ...canvasSize,
        fontEmbedCSS: fontEmbedCssRef.current,
      });
      if (generation !== generationRef.current) return;
      setSlotAt(pageKey, url ?? null);
    },
    [mainRef, setSlotAt],
  );

  const captureAll = useCallback(
    async (generation: number) => {
      const container = mainRef.current;
      if (!container || generation !== generationRef.current) return;
      const anchors = getScrollAnchors(container);
      if (anchors.length === 0) return;
      // First layout pass can be 0×0 (flex child not sized yet); skip rather
      // than writing empty thumbs — ResizeObserver will recapture.
      if (
        anchors.every(
          (element) => getReportPageCaptureElement(element).offsetWidth <= 0,
        )
      ) {
        return;
      }

      const first = anchors[0];
      if (first && fontEmbedCssRef.current === undefined) {
        // html-to-image's getFontEmbedCSS reads cssRules and console.errors a
        // SecurityError for cross-origin Google Fonts. Fetch those sheets by URL.
        try {
          fontEmbedCssRef.current = await buildReportThumbnailFontEmbedCss(
            getReportPageCaptureElement(first),
          );
        } catch {
          fontEmbedCssRef.current = '';
        }
      }

      for (let index = 0; index < anchors.length; index += 1) {
        if (generation !== generationRef.current) return;
        await captureIndex(index, generation);
      }
    },
    [captureIndex, mainRef],
  );

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    const followUpTimeouts: number[] = [];
    let cancelled = false;

    const cached = getReportThumbnailCache(
      contentKey,
      pageKeysRef.current,
      thumbWidthRef.current,
    );
    if (cached) {
      setUrlsByPageKey((prev) => ({ ...prev, ...cached.urlsByPageKey }));
    }
    const skipCapture = hasCompleteReportThumbnailCache(
      cached?.urlsByPageKey,
      pageKeysRef.current,
    );

    layoutReadyRef.current = false;

    const run = async () => {
      await Promise.race([
        document.fonts?.ready?.catch(() => undefined) ?? Promise.resolve(),
        sleep(300),
      ]);
      const container = mainRef.current;
      if (!container || cancelled) return;
      await waitForSettledPageLayout(container, () => cancelled);
      if (cancelled) return;

      const signature = getReportPagesLayoutSignature(
        getScrollAnchors(container),
      );
      // Same PNGs and the same page-box size: do not rasterize again. Wait
      // first so the ResizeObserver initial callback is ignored.
      const layoutUnchanged =
        skipCapture && cached?.layoutSignature === signature;

      layoutReadyRef.current = true;
      if (layoutUnchanged) return;

      await captureAll(generation);
      if (cancelled || generation !== generationRef.current) return;
      writeReportThumbnailCacheLayoutSignature(
        contentKey,
        pageKeysRef.current,
        thumbWidthRef.current,
        getReportPagesLayoutSignature(getScrollAnchors(container)),
      );

      getScrollAnchors(container).forEach((element, index) => {
        if (!element.querySelector('canvas')) return;
        for (const delayMs of CANVAS_FOLLOW_UP_MS) {
          followUpTimeouts.push(
            window.setTimeout(() => {
              if (cancelled) return;
              void captureIndex(index, generation);
            }, delayMs),
          );
        }
      });
    };

    void run();
    return () => {
      cancelled = true;
      // Drop in-flight toPng so leaving Report does not keep rasterizing.
      generationRef.current += 1;
      followUpTimeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [captureAll, captureIndex, contentKey, mainRef, pageKeysSignature]);

  const debouncedCaptureIndex = useDebouncedFunction((index: number) => {
    void captureIndex(index, generationRef.current);
  }, INPUT_DEBOUNCE_MS);

  const debouncedCaptureAll = useDebouncedFunction(() => {
    const container = mainRef.current;
    if (!container) return;
    const generation = generationRef.current;
    void (async () => {
      await waitForSettledPageLayout(
        container,
        () => generation !== generationRef.current,
      );
      await captureAll(generation);
    })();
  }, RESIZE_DEBOUNCE_MS);

  useEffect(() => {
    const container = mainRef.current;
    if (!container) return;

    const onInput = (event: Event) => {
      const index = getReportScrollAnchorIndex(event.target, container);
      if (index === undefined) return;
      debouncedCaptureIndex(index);
    };

    container.addEventListener('input', onInput, true);
    container.addEventListener('change', onInput, true);
    return () => {
      container.removeEventListener('input', onInput, true);
      container.removeEventListener('change', onInput, true);
    };
  }, [debouncedCaptureIndex, mainRef, pageKeysSignature]);

  useEffect(() => {
    const container = mainRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      if (!layoutReadyRef.current) return;
      debouncedCaptureAll();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [debouncedCaptureAll, mainRef, pageKeysSignature]);

  return pageKeys.map((key) => urlsByPageKey[key]);
};
